import React, { useState, useEffect } from 'react';
import {
  Typography,
  IconButton,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  Box,
  Button,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  ListItemText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Autocomplete,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Circle,
  CircleOutlined,
  Code,
  LocationOn,
  Email,
  Work,
  Business,
  WorkHistory,
  LinkedIn,
  PhoneAndroid,
  Language,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import Skype from '../../../assets/Skype';
import { useGetMasterDataQuery, useUpdateProfileMutation } from '../profileApiSlice';
import { capitalizeFirstLetter } from '../../../utils/capitalizeFirstLetter';
import toast from 'react-hot-toast';

export const ProfileDetail = ({ icon, label, value }) => (
  <ListItem disableGutters>
    <Typography width={"90%"} wordWrap={'break-word'} variant='body2'>
      <IconButton color='primary'>{icon}</IconButton>
      {label} {value || 'N/A'}
    </Typography>
  </ListItem>
);

export const ProfileCard = ({ user, onEdit, isCurrentUser }) => {
  const profileDetails = {
    name: user?.userName,
    email: user?.email,
    summary: user?.summary,
    officeLocation: user?.officeLocation,
  };

  const renderProfileDetails = details => {
    return details.map((detail, index) => (
      <ProfileDetail
        key={index}
        icon={detail.icon}
        label={detail.label}
        value={detail.value}
        onEdit={() => onEdit(detail.label, { [detail.field]: detail.value })}
        isCurrentUser={isCurrentUser}
      />
    ));
  };

  return (
    <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
      <Box display='flex' alignItems='center' flexDirection={'row'}>
        <List>
          {renderProfileDetails([
            { value: user?.summary, field: 'summary' },
            { icon: <Code />, label: 'Tech Stack:', value: user?.techStack, field: 'techStack' },
            {
              icon: <LocationOn />,
              label: 'Office Location:',
              value: user?.officeLocation,
              field: 'officeLocation',
            },
            { icon: <Email />, label: 'Email:', value: user?.email, field: 'email' },
            { icon: <Work />, label: 'Designation:', value: user?.designation, field: 'designation' },
            { icon: <Business />, label: 'Department:', value: user?.department, field: 'department' },
            {
              icon: <WorkHistory />,
              label: 'Experience:',
              value: `${Math.floor(user?.experience)} Years`,
              field: 'experience',
            },
            {
              icon: <Language />,
              label: 'Website:',
              value: user?.contact && user?.contact[0]?.personalWebsite,
            },
          ])}
        </List>
      </Box>
      {/* <Typography border={"1px solid black"}>
        {isCurrentUser && (
          <IconButton onClick={() => onEdit('Profile', profileDetails)}>
            <EditIcon />
          </IconButton>
        )}
      </Typography> */}
    </Box>
  );
};
export const RenderCard = ({
  title,
  items,
  showAll,
  showAllKey,
  sliceCount,
  renderItem,
  handleShowAllToggle,
  onEdit,
  onAdd,
  isCurrentUser,
}) => (
  <Grid item xs={12}>
    <Card sx={{ borderRadius: '10px', boxShadow: 'none', border: 1, borderColor: 'divider', flex: 1 }}>
      <CardContent>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='h6' fontWeight='bold'>
            {title}
          </Typography>
          {isCurrentUser && (
            <Box display='flex'>
              {onEdit && (
                <IconButton onClick={onEdit}>
                  <EditIcon />
                </IconButton>
              )}
              {onAdd && (
                <IconButton onClick={onAdd} sx={{ ml: 1 }}>
                  <AddIcon />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
        <List>
          {(showAll[showAllKey] ? items : items?.slice(0, sliceCount))?.map((item, index) => (
            <ListItem key={index} sx={{ py: 0.5 }}>
              {renderItem(item)}
            </ListItem>
          ))}
        </List>
      </CardContent>
      {items?.length > sliceCount && (
        <Box sx={{ display: 'flex', justifyContent: 'center', borderTop: '1px solid lightgrey' }}>
          <Button
            fullWidth
            onClick={() => handleShowAllToggle(showAllKey)}
            endIcon={<ArrowForwardIcon />}
            sx={{ fontWeight: 'bold' }}
          >
            {showAll[showAllKey] ? `Hide All ${title}` : `Show All ${items?.length} ${title}`}
          </Button>
        </Box>
      )}
    </Card>
  </Grid>
);

export const SkillRenderCard = ({ title, items, isCurrentUser, onEdit, onAdd }) => {
  return (
    <Card sx={{ borderRadius: '10px', boxShadow: 'none', border: 1, borderColor: 'divider', flex: 1 }}>
      <CardContent>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='h6' fontWeight='bold'>
            {title}
          </Typography>
          {isCurrentUser && (
            <Box display='flex'>
              {onEdit && (
                <IconButton onClick={onEdit}>
                  <EditIcon />
                </IconButton>
              )}
              {/* {onAdd && (
                <IconButton onClick={onAdd} sx={{ ml: 1 }}>
                  <AddIcon />
                </IconButton>
              )} */}
            </Box>
          )}
        </Box>
        {items?.map(category => (
          <Accordion key={category._id}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant='body1'>{category.category}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {category?.skills?.map(skill => (
                <ListItem
                  key={skill._id}
                  sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
                >
                  <Typography>{skill.name}</Typography>
                  <Typography>
                    <ProficiencyDots proficiency={skill.proficiency} />
                  </Typography>
                </ListItem>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </CardContent>
    </Card>
  );
};

export const ProficiencyDots = ({ proficiency }) => {
  const filledDots = proficiency === 'Advanced' ? 5 : proficiency === 'Intermediate' ? 3 : 2;
  const totalDots = 5;

  return (
    <Box>
      {Array.from({ length: filledDots }).map((_, index) => (
        <Circle fontSize='12px' key={`filled-${index}`} color='primary' />
      ))}
      {Array.from({ length: totalDots - filledDots }).map((_, index) => (
        <CircleOutlined fontSize='12px' key={`unfilled-${index}`} color='primary' />
      ))}
    </Box>
  );
};

export const ContactCard = ({ contact, handleEditClick, isCurrentUser, onAdd }) => (
  <Card sx={{ borderRadius: '10px', boxShadow: 'none', border: 1, borderColor: 'divider' }}>
    <CardContent>
      <Typography variant='h6' fontWeight='bold' display='flex' justifyContent='space-between'>
        Contact
        {isCurrentUser && (
          <Typography>
            {isCurrentUser && (
              <IconButton onClick={() => handleEditClick('Contact', { contact })}>
                <EditIcon />
              </IconButton>
            )}
            {onAdd && (
              <IconButton onClick={onAdd} sx={{ ml: 1 }}>
                <AddIcon />
              </IconButton>
            )}
          </Typography>
        )}
      </Typography>
      <>
        <ProfileDetail
          icon={<PhoneAndroid />}
          label='Phone:'
          value={contact && contact[0]?.phoneNumber}
          isCurrentUser={isCurrentUser}
        />
        <ProfileDetail
          icon={<LinkedIn />}
          label='LinkedIn:'
          value={contact && contact[0]?.linkedinUrl}
          isCurrentUser={isCurrentUser}
        />
        <ProfileDetail
          icon={<Language />}
          label='Website:'
          value={contact && contact[0]?.personalWebsite}
          isCurrentUser={isCurrentUser}
        />
        <ProfileDetail
          icon={<Skype />}
          label='Skype:'
          value={contact && contact[0]?.skypeId}
          isCurrentUser={isCurrentUser}
        />
      </>
    </CardContent>
  </Card>
);

export const LanguagesCard = ({ languages, handleEditClick, isCurrentUser, onAdd }) => (
  <Card sx={{ mt: 2, borderRadius: '8px', boxShadow: 'none', border: 1, borderColor: 'divider', height: '22vh' }}>
    <CardContent>
      <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
        <Typography fontWeight={'bold'} variant='h6'>
          Languages
        </Typography>
        <Typography>
          {isCurrentUser && (
            <IconButton onClick={() => handleEditClick('Languages', { languages })}>
              <EditIcon />
            </IconButton>
          )}
          {onAdd && (
            <IconButton onClick={onAdd} sx={{ ml: 1 }}>
              <AddIcon />
            </IconButton>
          )}
        </Typography>
      </Box>
      <List sx={{ overflow: 'auto', height: '14vh' }}>
        {languages?.map((language, index) => (
          <ListItem key={index} sx={{ py: 0 }}>
            <ListItemText primary={language.language} secondary={language.proficiency} />
          </ListItem>
        ))}
      </List>
    </CardContent>
  </Card>
);

export const EditDialog = ({
  open,
  dialogTitle,
  editData,
  handleDialogClose,
  handleInputChange,
  handleSave,
  handleDelete,
  handleCertificationChange,
}) => {
  const renderFields = () => {
    if (!editData) return null;

    const formatDate = date => {
      return new Date(date).toISOString().split('T')[0];
    };

    return Object.keys(editData).map(key => {
      if (key === 'certifications') {
        return (
          <Autocomplete
            key={key}
            multiple
            options={certificationsData}
            value={editData[key] || []}
            onChange={(e, newValue) => handleCertificationChange(newValue)}
            renderInput={params => <TextField {...params} variant='outlined' label='Certifications' fullWidth />}
          />
        );
      } else if (Array.isArray(editData[key])) {
        return editData[key].map((item, index) => (
          <Accordion key={index}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant='subtitle1'>
                {key === 'projects' ? item.domain : `${key.charAt(0).toUpperCase() + key.slice(1)} ${index + 1}`}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {Object.keys(item).map(field =>
                field === '_id' ? null : (
                  <TextField
                    key={field}
                    margin='dense'
                    name={`${key}[${index}].${field}`}
                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                    type={field.includes('Date') ? 'date' : 'text'}
                    fullWidth
                    value={field.includes('Date') ? formatDate(item[field]) : item[field]}
                    onChange={handleInputChange}
                    InputLabelProps={field.includes('Date') ? { shrink: true } : {}}
                  />
                )
              )}
              {key == 'contact' ? (
                <></>
              ) : (
                <IconButton onClick={() => handleDelete(key, index)} aria-label='delete'>
                  <DeleteIcon />
                </IconButton>
              )}
            </AccordionDetails>
          </Accordion>
        ));
      } else {
        return (
          <TextField
            key={key}
            margin='dense'
            name={key}
            label={key.replace(/([A-Z])/g, ' $1').trim()}
            type={key === 'date' ? 'date' : 'text'}
            fullWidth
            value={editData[key]}
            onChange={handleInputChange}
            InputLabelProps={key === 'date' ? { shrink: true } : {}}
            required={true}
          />
        );
      }
    });
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth='md'>
      <DialogTitle>
        <Typography variant='h6' fontWeight='bold'>
          {dialogTitle}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box mt={2}>{renderFields()}</Box>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button onClick={handleDialogClose} variant='outlined' color='primary'>
          Cancel
        </Button>
        <Button onClick={handleSave} variant='contained' color='primary'>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export const EditSkillsDialog = ({
  open,
  dialogTitle,
  editData,
  handleDialogClose,
  handleDelete,
  handleAddData,
  id,
  setOpenDialog,
  setOpenSkillsDialog,
}) => {
  const [updateProfile] = useUpdateProfileMutation();
  const [localEditData, setLocalEditData] = useState([]);
  const [selectedPrimarySkill, setSelectedPrimarySkill] = useState(null);
  const [technologyId, setTechnologyId] = useState(0);
  const { data: masterData } = useGetMasterDataQuery({ type: 1, technologyId: 0 });
  const { data: secondarySkillsData, refetch: refetchSecondarySkills } = useGetMasterDataQuery({
    type: 0,
    technologyId,
  });
  const [secondarySkills, setSecondarySkills] = useState([]);
  const [primarySkills, setPrimarySkills] = useState([]);

  useEffect(() => {
    if (editData) {
      setLocalEditData(editData);
    }
  }, [editData]);

  useEffect(() => {
    if (masterData?.data?.gsmsOthers) {
      setPrimarySkills(masterData.data.gsmsOthers);
    }
  }, [masterData]);

  useEffect(() => {
    if (selectedPrimarySkill !== null) {
      setTechnologyId(selectedPrimarySkill);
      refetchSecondarySkills();
    }
  }, [selectedPrimarySkill]);

  useEffect(() => {
    if (secondarySkillsData?.data?.gsmspsSkills) {
      setSecondarySkills(secondarySkillsData.data.gsmspsSkills);
    }
  }, [secondarySkillsData]);

  const handleSave = async () => {
    const formattedData = localEditData.map(category => ({
      ...category,
      skills: category.skills.map(skill => ({
        name: skill.name,
        proficiency: skill.proficiency,
      })),
    }));

    await updateProfile({ empCode: id, data: { skills: formattedData } });
    toast.success('Profile updated successfully!');
    setOpenDialog(false);
    setOpenSkillsDialog(false);
  };

  const handlePrimarySkillSelect = (selectedSkillId, categoryIndex) => {
    const updatedData = localEditData.map((item, index) => {
      if (index === categoryIndex) {
        const selectedSkill = primarySkills.find(skill => skill.technologyId === selectedSkillId);
        if (selectedSkill) {
          return {
            ...item,
            category: selectedSkill.technology,
            skills: item.skills.map(skill => ({ ...skill })),
          };
        }
      }
      return item;
    });

    setSelectedPrimarySkill(selectedSkillId);
    setLocalEditData(updatedData);
  };

  const handleAccordionClick = category => {
    const selectedSkilll = primarySkills.find(skill => skill.technology === category?.category);
    setSelectedPrimarySkill(selectedSkilll?.technologyId);
  };

  const handleSecondarySkillSelect = (selectedSkill, categoryIndex, skillIndex) => {
    const updatedData = localEditData.map((item, cIndex) => {
      if (cIndex === categoryIndex) {
        return {
          ...item,
          skills: item.skills.map((skill, sIndex) => {
            if (sIndex === skillIndex) {
              return { ...skill, name: selectedSkill };
            }
            return skill;
          }),
        };
      }
      return item;
    });

    setLocalEditData(updatedData);
  };

  const handleAddPrimarySkill = () => {
    const newCategory = {
      category: '',
      skills: [{ name: '', proficiency: '' }],
    };
    setLocalEditData([...localEditData, newCategory]);
  };

  const handleProficiencyChange = (event, categoryIndex, skillIndex) => {
    const updatedData = localEditData.map((item, cIndex) => {
      if (cIndex === categoryIndex) {
        return {
          ...item,
          skills: item.skills.map((skill, sIndex) => {
            if (sIndex === skillIndex) {
              return { ...skill, proficiency: event.target.value };
            }
            return skill;
          }),
        };
      }
      return item;
    });

    setLocalEditData(updatedData);
  };

  const renderFields = () => {
    if (dialogTitle !== 'Edit Skills') {
      return null;
    }

    return localEditData?.map((category, categoryIndex) => (
      <Accordion key={categoryIndex} sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} onClick={() => handleAccordionClick(category, categoryIndex)}>
          <Typography variant='subtitle1'>{category.category || 'New Category'}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <PrimarySkillSelect
            skills={primarySkills}
            selectedSkill={
              category.category ? primarySkills.find(skill => skill.technology === category.category)?.technologyId : ''
            }
            onSelect={selectedSkillId => handlePrimarySkillSelect(selectedSkillId, categoryIndex)}
          />
          {category.skills.map((skill, skillIndex) => (
            <Box key={skillIndex} mb={2}>
              <SecondarySkillSelect
                skills={secondarySkills}
                selectedSkill={skill.name}
                onSelect={selectedSkill => handleSecondarySkillSelect(selectedSkill, categoryIndex, skillIndex)}
              />
              <FormControl fullWidth margin='dense'>
                <InputLabel>Proficiency</InputLabel>
                <Select
                  value={skill.proficiency || ''}
                  onChange={e => handleProficiencyChange(e, categoryIndex, skillIndex)}
                  label='Proficiency'
                >
                  <MenuItem value='Beginner'>Beginner</MenuItem>
                  <MenuItem value='Intermediate'>Intermediate</MenuItem>
                  <MenuItem value='Advanced'>Advanced</MenuItem>
                </Select>
              </FormControl>
              <IconButton
                onClick={() => handleDelete('skills', categoryIndex, skillIndex)}
                aria-label='delete'
                color='error'
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          <Button onClick={() => handleAddData('skills', categoryIndex, category)} variant='contained' sx={{ mt: 2 }}>
            Add Skill
          </Button>
        </AccordionDetails>
      </Accordion>
    ));
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth='md'>
      <DialogTitle>
        <Typography fontWeight={'bold'}>{dialogTitle}</Typography>
      </DialogTitle>
      <DialogContent>
        <Box mt={2}>
          {renderFields()}
          <Button onClick={handleAddPrimarySkill} variant='contained' sx={{ mt: 2 }}>
            Add Primary Skill
          </Button>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button onClick={handleDialogClose} variant='outlined' color='primary'>
          Cancel
        </Button>
        <Button onClick={handleSave} variant='contained' color='primary'>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const PrimarySkillSelect = ({ skills, selectedSkill, onSelect }) => {
  return (
    <FormControl fullWidth sx={{ mb: 1.5 }}>
      <Autocomplete
        options={skills || []}
        getOptionLabel={option => option.technology}
        value={skills.find(skill => skill.technologyId === selectedSkill) || null}
        onChange={(event, newValue) => onSelect(newValue ? newValue.technologyId : '')}
        renderInput={params => <TextField {...params} label='Select Primary Skill' variant='outlined' />}
        variant='outlined'
      />
    </FormControl>
  );
};

export const SecondarySkillSelect = ({ skills, selectedSkill, onSelect }) => {
  return (
    <FormControl fullWidth sx={{ mb: 1.5 }}>
      <Autocomplete
        options={skills || []}
        getOptionLabel={option => option.subTechnology}
        value={skills.find(skill => skill.subTechnology === selectedSkill) || null}
        onChange={(event, newValue) => onSelect(newValue ? newValue.subTechnology : '')}
        renderInput={params => <TextField {...params} label='Select Secondary Skill' variant='outlined' />}
      />
    </FormControl>
  );
};

export const AddDataDialog = ({ open, dialogTitle, addData, handleDialogClose, handleInputChange, handleSave }) => {
  const renderFields = () => {
    if (!addData) return null;

    const sectionKey = Object.keys(addData)[0];
    if (!sectionKey || !addData[sectionKey]) return null;

    const emptyData = addData[sectionKey][0];
    if (!emptyData) return null;

    return Object.keys(emptyData).map((field, index) => (
      <TextField
        key={index}
        margin='dense'
        name={`${sectionKey}[0].${field}`}
        type={field.includes('Date') ? 'date' : 'text'}
        label={capitalizeFirstLetter(field)}
        fullWidth
        variant='outlined'
        onChange={handleInputChange}
        InputLabelProps={field.includes('Date') ? { shrink: true } : {}}
      />
    ));
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth='md' fullWidth>
      <DialogTitle fontWeight={'bold'}>{dialogTitle}</DialogTitle>
      <DialogContent>{renderFields()}</DialogContent>
      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button onClick={handleDialogClose} variant='outlined' color='primary'>
          Cancel
        </Button>
        <Button onClick={handleSave} variant='contained' color='primary'>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const certificationsData = [
  '70-480: Programming in HTML5 with JavaScript and CSS3',
  '70-483: Programming in C#',
  '70-487: Developing Microsoft Azure and Web Services',
  'Adobe Dreamweaver CS3',
  'Adobe Flash CS3',
  'Adobe Illustrator',
  'Adobe InDesign CS3',
  'Adobe PhotoShop CS3',
  'Adobe suit',
  'AWS Certified Cloud Practioner',
  'AWS Certified Solutions Architect - Associate',
  'AWS Professional',
  'AZ-203: Developing Solutions for Microsoft Azure (released December 27, 2018)',
  'AZ-204: Developing Solutions for Microsoft Azure (released December 27, 2018)',
  'AZ-900: Microsoft Azure Fundamentals (released December 31, 2018)',
  'BrainBench',
  'CASQ',
  'CAST',
  'CCNA',
  'Certified Multicloud Architect Associate',
  'Cloud Digital Leader (GCP cloud)',
  'CMSQ',
  'CMST',
  'CSBA',
  'CSPE',
  'CSPM',
  'CSQA',
  'CSTE',
  'CSTM',
  'Dp-300 Azure Database Administrator Associate',
  'DP-900- Azure data fundamentals',
  'GoLang',
  'Google Analytics Individual Qualification (GAIQ)',
  'Google Cloud Certification: Associate Cloud Developer',
  'Google Cloud Certification: Professional Cloud Developer',
  'HP-QC',
  'HP-QTP',
  'HTML5',
  'ISTQB - AL',
  'ISTQB Agile Tester Extension',
  'ISTQB Foundation level',
  'ISTQB Foundation Level 2018',
  'ISTQB Test Automation Engineer',
  'ISTQB-FL',
  'Magento Certification',
  'MB-210: Microsoft Dynamics 365 Sales',
  'MB2-716: Microsoft Dynamics 365 Customization and Configuration',
  'MB-910: Microsoft Dynamics 365 Fundamentals',
  'MCAD',
  'MCDBA',
  'MCDST',
  'MCITP',
  'MCP',
  'MCPD',
  'MCPSB',
  'MCPSI',
  'MCSA',
  'MCSAM',
  'MCSAS',
  'MCSD',
  'MCSE',
  'MCTS',
  'Microsoft Certified: Azure AI Engineer Associate',
  'MySQL',
  'OCP',
  'QTP',
  'RHCE',
  'RHCVA',
  'Salesforce Developer',
  'SCJP',
  'Tosca Fundamentals Automating Web Application Testing (AS1)',
  'Tricentis Tosca Fundamentals − Optimizing Test Automation with Centralized Test Data',
  'VMware',
  'Zend',
];
